import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Rating,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Star,
  LocationOn,
  WorkOutline,
  Schedule,
  AttachMoney,
  Build,
  TrendingUp,
  Message
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import messagingService from '../services/messagingService';
import DialogCloseButton from '../components/common/DialogCloseButton';

const VerificationProposalsPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [verificationRequest, setVerificationRequest] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);

  // Safe JSON parsing helper
  const safeJsonParse = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  useEffect(() => {
    fetchVerificationRequest();
    fetchProposals();
  }, [requestId]);

  const fetchVerificationRequest = async () => {
    try {
      const response = await api.get(`/verification-requests/${requestId}`);
      if (response.data.success) {
        setVerificationRequest(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching verification request:', err);
    }
  };

  const fetchProposals = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(
        `/client/verification-requests/${requestId}/proposals`,
        { params: { clientId: user.id } }
      );
      if (response.data.success) {
        setProposals(response.data.data || []);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch responses');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (proposal) => {
    setSelectedProposal(proposal);
    setDetailDialog(true);

    // Mark as viewed
    try {
      await api.put(
        `/client/verification-requests/proposals/${proposal.id}/view`,
        { clientId: user.id }
      );
      // Refresh proposals to update viewed status
      fetchProposals();
    } catch (err) {
      console.error('Error marking proposal as viewed:', err);
    }
  };

  const handleAcceptProposal = async (proposalId) => {
    if (!window.confirm('Are you sure you want to accept this response? This will assign the verifier to your verification request.')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await api.post(
        `/client/verification-requests/proposals/${proposalId}/accept`,
        { clientId: user.id }
      );

      if (response.data.success) {
        setSuccess('Response accepted. The verifier has been assigned and notified.');
        setDetailDialog(false);
        // Refresh responses to show updated status (accepted/rejected)
        fetchProposals();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to accept response');
    }
  };

  const handleMessageVerifier = async (proposal) => {
    try {
      setError('');
      const result = await messagingService.getOrCreateConversation(
        proposal.verifier_id,
        verificationRequest?.project_id || null
      );
      const conversationId = result.data?.id || result.id;
      navigate(`/messages/${conversationId}`);
    } catch (err) {
      console.error('Error opening verifier conversation:', err);
      setError('Failed to open messaging. Please try again.');
    }
  };

  const getVerifierInitials = (verifier) => {
    if (verifier.first_name && verifier.last_name) {
      return `${verifier.first_name[0]}${verifier.last_name[0]}`.toUpperCase();
    }
    return verifier.email[0].toUpperCase();
  };

  const renderProposalCard = (proposal) => {
    const equipmentList = safeJsonParse(proposal.equipment_list);
    const certifications = safeJsonParse(proposal.certifications_provided);

    return (
      <Card key={proposal.id} sx={{
        mb: 3,
        position: 'relative',
        ...(proposal.status === 'accepted' && {
          border: '2px solid',
          borderColor: 'success.main',
          bgcolor: 'success.50'
        }),
        ...(proposal.status === 'rejected' && {
          opacity: 0.6,
          bgcolor: 'grey.50'
        })
      }}>
        {!proposal.client_viewed && (
          <Chip
            label="New"
            color="primary"
            size="small"
            sx={{ position: 'absolute', top: 16, right: 16 }}
          />
        )}

        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              {getVerifierInitials(proposal)}
            </Avatar>
            <Box flex={1}>
              <Typography variant="h6">
                {proposal.first_name && proposal.last_name
                  ? `${proposal.first_name} ${proposal.last_name}`
                  : proposal.email}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Chip
                  label={proposal.tier}
                  size="small"
                  color={proposal.tier === 'professional' ? 'secondary' : 'default'}
                />
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Star fontSize="small" sx={{ color: 'gold' }} />
                  <Typography variant="body2">
                    {proposal.average_rating ? parseFloat(proposal.average_rating).toFixed(1) : 'N/A'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ({proposal.total_reviews || 0} reviews)
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Project Owner Budget and Quote Comparison */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Project Owner Budget
                </Typography>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                  ${parseFloat(verificationRequest?.estimated_fee || verificationRequest?.payment?.verificationFee || 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Verifier Fee
                </Typography>
                <Typography variant="h6" color={parseFloat(proposal.quote_amount) <= parseFloat(verificationRequest?.estimated_fee || verificationRequest?.payment?.verificationFee || 0) ? 'success.main' : 'warning.main'} sx={{ fontWeight: 'bold' }}>
                  ${parseFloat(proposal.quote_amount).toFixed(2)} {proposal.currency}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Acceptance/Rejection Indicator */}
          {proposal.status && (
            <Box sx={{ mb: 2 }}>
              {proposal.status === 'accepted' && (
                <Chip
                  icon={<CheckCircle />}
                  label="Accepted"
                  color="success"
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {proposal.status === 'rejected' && (
                <Chip
                  icon={<Cancel />}
                  label="Rejected"
                  color="error"
                  size="medium"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {proposal.status === 'submitted' && (
                <Chip
                  label="Pending Review"
                  color="info"
                  size="medium"
                />
              )}
              {proposal.status === 'withdrawn' && (
                <Chip
                  label="Withdrawn"
                  color="default"
                  size="medium"
                />
              )}
            </Box>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <Schedule fontSize="small" color="primary" />
                <Typography variant="body2">
                  <strong>Est. Hours:</strong> {proposal.estimated_hours}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <WorkOutline fontSize="small" color="primary" />
                <Typography variant="body2">
                  <strong>Completed:</strong> {proposal.completed_verifications || 0} verifications
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUp fontSize="small" color="primary" />
                <Typography variant="body2">
                  <strong>Success Rate:</strong> {proposal.completion_rate ? `${proposal.completion_rate}%` : 'N/A'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box display="flex" alignItems="center" gap={1}>
                <LocationOn fontSize="small" color="primary" />
                <Typography variant="body2">
                  <strong>Based in:</strong> {proposal.base_city || 'Not specified'}
                </Typography>
              </Box>
            </Grid>

            {proposal.available_date && (
              <Grid item xs={12} sm={6}>
                <Typography variant="body2">
                  <strong>Available:</strong> {new Date(proposal.available_date).toLocaleDateString()}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Response:
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {proposal.proposal_text.substring(0, 200)}
            {proposal.proposal_text.length > 200 && '...'}
          </Typography>

          {equipmentList.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary">
                <Build fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Equipment: {equipmentList.slice(0, 3).join(', ')}
                {equipmentList.length > 3 && '...'}
              </Typography>
            </Box>
          )}
        </CardContent>

        <CardActions>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircle />}
            onClick={() => handleAcceptProposal(proposal.id)}
            disabled={proposal.status !== 'submitted'}
          >
            Accept
          </Button>
          <Button
            startIcon={<Visibility />}
            onClick={() => handleViewDetails(proposal)}
          >
            View Full Details
          </Button>
          <Button
            startIcon={<Message />}
            onClick={() => handleMessageVerifier(proposal)}
          >
            Message Verifier
          </Button>
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        ← Back
      </Button>

      <Typography variant="h4" gutterBottom>
        Verifier Responses
      </Typography>

      {verificationRequest && (
        <Card sx={{ mb: 3, bgcolor: 'background.default' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {verificationRequest.request_details?.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {verificationRequest.request_details?.description}
            </Typography>
            <Box display="flex" gap={2} mt={2}>
              <Chip
                label={`${proposals.length} Responses Received`}
                color="primary"
                size="small"
              />
              {verificationRequest.required_tier && (
                <Chip
                  label={`${verificationRequest.required_tier} Tier Required`}
                  size="small"
                />
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/verification-requests')}
            >
              ← Back to My Verifications
            </Button>
          }
        >
          {success}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : proposals.length === 0 ? (
        <Alert severity="info">
          No responses received yet. Verifiers can respond from the Verification Jobs page.
        </Alert>
      ) : (
        <Box>
          {proposals.map(proposal => renderProposalCard(proposal))}
        </Box>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setDetailDialog(false)} />
        <DialogTitle>
          Verifier Response
        </DialogTitle>
        <DialogContent>
          {selectedProposal && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64 }}>
                  {getVerifierInitials(selectedProposal)}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedProposal.first_name && selectedProposal.last_name
                      ? `${selectedProposal.first_name} ${selectedProposal.last_name}`
                      : selectedProposal.email}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Rating value={selectedProposal.average_rating || 0} readOnly size="small" />
                    <Typography variant="body2">
                      ({selectedProposal.total_reviews || 0} reviews)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Quote Amount</Typography>
                  <Typography variant="h6">
                    ${parseFloat(selectedProposal.quote_amount).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Estimated Hours</Typography>
                  <Typography variant="h6">{selectedProposal.estimated_hours}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Experience</Typography>
                  <Typography variant="body1">
                    {selectedProposal.years_experience} years
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Completion Rate</Typography>
                  <Typography variant="body1">
                    {selectedProposal.completion_rate ? `${selectedProposal.completion_rate}%` : 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Response
              </Typography>
              <Typography variant="body2" paragraph>
                {selectedProposal.proposal_text}
              </Typography>

              {selectedProposal.approach && (
                <>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Approach
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedProposal.approach}
                  </Typography>
                </>
              )}

              {(() => {
                const equipment = safeJsonParse(selectedProposal.equipment_list);
                return equipment.length > 0 && (
                  <>
                    <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                      Equipment
                    </Typography>
                    <List dense>
                      {equipment.map((item, idx) => (
                        <ListItem key={idx}>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </>
                );
              })()}

              {(() => {
                const specs = safeJsonParse(selectedProposal.specializations);
                return specs.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mt: 2 }}>
                    Specializations
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {specs.map((spec, idx) => (
                      <Chip key={idx} label={spec} size="small" />
                    ))}
                  </Box>
                </>
              );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>
            Close
          </Button>
          {selectedProposal && selectedProposal.status === 'submitted' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleAcceptProposal(selectedProposal.id)}
            >
              Accept Response
            </Button>
          )}
          {selectedProposal && (
            <Button
              startIcon={<Message />}
              onClick={() => handleMessageVerifier(selectedProposal)}
            >
              Message Verifier
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VerificationProposalsPage;
